import { useSyncExternalStore } from "react";

/**
 * "Your workspace is paused" — remembered for the sign-in screen.
 *
 * Set when People refuses this browser's session because the organization is
 * restricted for an unpaid bill (a refresh or a request answered `403
 * workspace_paused`). The shell then clears auth and lands on LoginScreen,
 * which reads this to explain why, instead of looking like a crash.
 *
 * Kept in sessionStorage so the explanation survives the reload a sign-out can
 * cause, but not a new browser session. Deliberately independent of the shell
 * store, which imports it.
 */

const KEY = "weco-workspace-paused";
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

let paused = read();

function set(next: boolean): void {
  try {
    if (next) sessionStorage.setItem(KEY, "1");
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable: the in-memory flag still works for this page */
  }
  if (paused === next) return;
  paused = next;
  listeners.forEach((fn) => fn());
}

export function markWorkspacePaused(): void {
  set(true);
}

export function clearWorkspacePausedNotice(): void {
  set(false);
}

export function isWorkspacePausedNoticeSet(): boolean {
  return paused;
}

export function subscribeWorkspacePausedNotice(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useWorkspacePausedNotice(): boolean {
  return useSyncExternalStore(subscribeWorkspacePausedNotice, isWorkspacePausedNoticeSet, isWorkspacePausedNoticeSet);
}
